import { useEffect, useRef } from 'react';
import { socket } from '~/constants/sockets/api';
import { User } from '~/types';
import { useToast } from '~/contexts/Toast';
import { getAchievementBadge } from '~/components/AchievementItem/achievementBadges';
import { achievementTypeToId } from '~/constants/defaultValues';
import {
  useKeyContext,
  useMissionContext,
  useNotiContext,
  useAppContext,
  useContentContext,
  useManagementContext
} from '~/contexts';
import {
  clearAchievementProgressEventMemory,
  consumeAchievementProgressEvent
} from './achievementProgressEventDedupe';
import { applyCanonicalCoinsAndReconcile } from '~/helpers/canonicalUserCoins';
import {
  collectObservedSubjectIds,
  getMountedSubjectIds,
  refreshSubjectRewardLevelBatches
} from '~/helpers/subjectRewardLevelSync';

interface AdminTelemetryEvent {
  message?: string;
  notifyAdmin?: boolean;
  timestamp?: number;
}

export default function useNotiSocket({
  onUpdateMyXp
}: {
  onUpdateMyXp: () => void;
}) {
  const onUpdateMyXpRef = useRef(onUpdateMyXp);
  onUpdateMyXpRef.current = onUpdateMyXp;

  const userId = useKeyContext((v) => v.myState.userId);
  const onAddAdminTelemetry = useManagementContext(
    (v) => v.actions.onAddAdminTelemetry
  );
  const onAttachReward = useContentContext((v) => v.actions.onAttachReward);
  const onCloseContent = useContentContext((v) => v.actions.onCloseContent);
  const onEditContent = useContentContext((v) => v.actions.onEditContent);
  const onIncreaseNumNewNotis = useNotiContext(
    (v) => v.actions.onIncreaseNumNewNotis
  );
  const onIncreaseNumNewPosts = useNotiContext(
    (v) => v.actions.onIncreaseNumNewPosts
  );
  const onLikeContent = useContentContext((v) => v.actions.onLikeContent);
  const onLoadNotifications = useNotiContext(
    (v) => v.actions.onLoadNotifications
  );
  const onLoadRewards = useNotiContext((v) => v.actions.onLoadRewards);
  const onOpenContent = useContentContext((v) => v.actions.onOpenContent);
  const onRecommendContent = useContentContext(
    (v) => v.actions.onRecommendContent
  );
  const onSetRewardLevel = useContentContext((v) => v.actions.onSetRewardLevel);
  const onSetUserState = useAppContext((v) => v.user.actions.onSetUserState);
  const onUpdateAchievementUnlockStatus = useAppContext(
    (v) => v.user.actions.onUpdateAchievementUnlockStatus
  );
  const onSetRewardsTimeoutExecuted = useNotiContext(
    (v) => v.actions.onSetRewardsTimeoutExecuted
  );
  const onUpdateMissionAttempt = useMissionContext(
    (v) => v.actions.onUpdateMissionAttempt
  );
  const onUploadComment = useContentContext((v) => v.actions.onUploadComment);
  const onUploadReply = useContentContext((v) => v.actions.onUploadReply);
  const getContentStateSnapshot = useContentContext(
    (v) => v.getContentStateSnapshot
  );

  const fetchNotifications = useAppContext(
    (v) => v.requestHelpers.fetchNotifications
  );
  const loadCoins = useAppContext((v) => v.requestHelpers.loadCoins);
  const loadRewards = useAppContext((v) => v.requestHelpers.loadRewards);
  const loadSubjectRewardLevels = useAppContext(
    (v) => v.requestHelpers.loadSubjectRewardLevels
  );

  const subjectRewardLevelRefreshInFlightRef = useRef(false);
  // One return to the tab fires connect, visibilitychange and pageshow; a
  // refresh that fully succeeded covers the others for 15 s. A failed one
  // (network not back yet) leaves the next trigger free to retry.
  const subjectRewardLevelRefreshedAtRef = useRef(0);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  useEffect(() => {
    async function refreshObservedSubjectRewardLevels() {
      if (
        document.visibilityState !== 'visible' ||
        subjectRewardLevelRefreshInFlightRef.current ||
        Date.now() - subjectRewardLevelRefreshedAtRef.current < 15000
      ) {
        return;
      }
      const subjectIds = collectObservedSubjectIds(
        getContentStateSnapshot(),
        getMountedSubjectIds()
      );
      if (subjectIds.length === 0) return;

      subjectRewardLevelRefreshInFlightRef.current = true;
      const startedAt = Date.now();
      let failed = false;
      const requestUserId = userId;
      try {
        await refreshSubjectRewardLevelBatches({
          subjectIds,
          loadSubjectRewardLevels,
          shouldContinue: () => requestUserId === userIdRef.current,
          onSubject: ({ contentId, rewardLevel, rewardLevelRevision }) => {
            onSetRewardLevel({
              contentId,
              contentType: 'subject',
              rewardLevel,
              rewardLevelRevision
            });
          },
          onBatchError: (error) => {
            failed = true;
            console.error(
              'Failed to refresh a subject effort-level batch after resume:',
              error
            );
          }
        });
        if (!failed && requestUserId === userIdRef.current) {
          subjectRewardLevelRefreshedAtRef.current = startedAt;
        }
      } finally {
        subjectRewardLevelRefreshInFlightRef.current = false;
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        void refreshObservedSubjectRewardLevels();
      }
    }

    socket.on('connect', refreshObservedSubjectRewardLevels);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', refreshObservedSubjectRewardLevels);
    return () => {
      socket.off('connect', refreshObservedSubjectRewardLevels);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener(
        'pageshow',
        refreshObservedSubjectRewardLevels
      );
    };
    // getContentStateSnapshot, loadSubjectRewardLevels, and onSetRewardLevel
    // are stable context helpers; userId is the only changing input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const showToast = useToast();
  const achievementsObj = useAppContext((v) => v.user.state.achievementsObj);
  const achievementsObjRef = useRef(achievementsObj);
  achievementsObjRef.current = achievementsObj;
  const unlockedAchievementIds = useKeyContext(
    (v) => v.myState.unlockedAchievementIds
  );
  const unlockedAchievementIdsRef = useRef(unlockedAchievementIds);
  unlockedAchievementIdsRef.current = unlockedAchievementIds;
  // Last progress value seen per achievement type. Doubles as a dedup guard and
  // the baseline for the "+delta" the user just gained.
  const progressByTypeRef = useRef<Record<string, number>>({});

  // Clear the per-type progress cache on account switch in the same tab, so user
  // B never inherits user A's dedup/baseline values (which would wrongly
  // suppress a toast or animate from A's old value).
  useEffect(() => {
    progressByTypeRef.current = {};
    clearAchievementProgressEventMemory();
  }, [userId]);

  useEffect(() => {
    socket.on('build_deleted', handleBuildDeleted);
    socket.on('content_closed', handleContentClose);
    socket.on('content_edited', handleEditContent);
    socket.on('content_opened', handleContentOpen);
    socket.on('achievement_progress', handleAchievementProgress);
    socket.on('mission_rewards_received', handleMissionRewards);
    socket.on('new_notification_received', handleNewNotification);
    socket.on('admin_telemetry_received', handleAdminTelemetry);
    socket.on('new_post_uploaded', handleNewPost);
    socket.on('new_reward_posted', handleNewReward);
    socket.on('new_recommendation_posted', handleNewRecommendation);

    return function cleanUp() {
      socket.off('achievement_progress', handleAchievementProgress);
      socket.off('build_deleted', handleBuildDeleted);
      socket.off('content_closed', handleContentClose);
      socket.off('content_edited', handleEditContent);
      socket.off('content_opened', handleContentOpen);
      socket.off('mission_rewards_received', handleMissionRewards);
      socket.off('admin_telemetry_received', handleAdminTelemetry);
      socket.off('new_notification_received', handleNewNotification);
      socket.off('new_post_uploaded', handleNewPost);
      socket.off('new_reward_posted', handleNewReward);
      socket.off('new_recommendation_posted', handleNewRecommendation);
    };

    function handleAdminTelemetry(event: AdminTelemetryEvent | string) {
      const message = typeof event === 'string' ? event : event?.message;
      if (!message) return;
      const timestamp =
        typeof event === 'object' && Number(event.timestamp) > 0
          ? Number(event.timestamp)
          : Date.now();
      onAddAdminTelemetry({
        message,
        notifyAdmin:
          typeof event === 'object' ? Boolean(event.notifyAdmin) : false,
        timestamp
      });
    }

    async function handleBuildDeleted({ buildIds }: { buildIds?: number[] }) {
      if (!userId || !Array.isArray(buildIds) || buildIds.length === 0) {
        return;
      }
      const { currentChatSubject, loadMoreNotifications, notifications } =
        await fetchNotifications({ fromWriter: true });
      onLoadNotifications({
        currentChatSubject,
        loadMoreNotifications,
        notifications,
        preserveUnreadCount: true,
        userId
      });
    }

    function handleContentClose({
      contentId,
      contentType,
      closedBy
    }: {
      contentId: number;
      contentType: string;
      closedBy: User;
    }) {
      onCloseContent({ contentId, contentType, userId: closedBy });
    }

    function handleContentOpen({
      contentId,
      contentType
    }: {
      contentId: number;
      contentType: string;
    }) {
      onOpenContent({ contentId, contentType });
    }

    function handleEditContent({
      contentType,
      contentId,
      newState
    }: {
      contentType: string;
      contentId: number;
      newState: any;
    }) {
      onEditContent({
        contentType,
        contentId,
        data: newState
      });
    }

    function handleMissionRewards({
      includesCoinReward,
      includesXpReward,
      missionId
    }: {
      includesCoinReward: boolean;
      includesXpReward: boolean;
      missionId: number;
    }) {
      if (includesCoinReward) {
        handleUpdateMyCoins();
      }
      if (includesXpReward) {
        onUpdateMyXpRef.current();
      }
      onUpdateMissionAttempt({
        missionId,
        newState: { status: 'pass', tryingAgain: false }
      });
    }

    function handleNewNotification({
      type,
      achievementType,
      isAchievementUnlocked,
      likes,
      target
    }: {
      type: string;
      achievementType: string;
      isAchievementUnlocked: boolean;
      likes: any[];
      target: any;
    }) {
      if (type === 'achievement') {
        onUpdateAchievementUnlockStatus({
          userId,
          achievementType,
          isUnlocked: isAchievementUnlocked
        });
        if (isAchievementUnlocked) {
          const achievement = achievementsObjRef.current?.[achievementType];
          if (achievement) {
            showToast({
              kind: 'achievement',
              mode: 'unlock',
              title: achievement.title,
              badgeSrc: getAchievementBadge(achievementType),
              ap: achievement.ap,
              linkTo: `/achievements/${achievementType}`,
              duration: 6500
            });
          }
        }
      }
      if (likes) {
        onLikeContent({
          likes,
          contentId: target.contentId,
          contentType: target.contentType
        });
      }
      if (type !== 'achievement' || isAchievementUnlocked) {
        onIncreaseNumNewNotis();
      }
    }

    function handleAchievementProgress({
      type,
      currentValue,
      targetValue,
      eventId
    }: {
      type: string;
      currentValue: number;
      targetValue: number;
      eventId?: string;
    }) {
      if (!type || !targetValue) return;
      // Never show progress for an achievement the user already owns (e.g. Gold
      // can re-enter the server's milestone branch if XP is revoked below 10M
      // then re-earned).
      const achievementId = achievementTypeToId[type];
      if (
        achievementId &&
        unlockedAchievementIdsRef.current?.includes(achievementId)
      ) {
        return;
      }
      const prev = progressByTypeRef.current[type];
      if (prev === currentValue) return;
      if (
        !consumeAchievementProgressEvent({
          userId,
          eventId
        })
      ) {
        return;
      }
      const delta =
        typeof prev === 'number' && currentValue > prev
          ? currentValue - prev
          : undefined;
      progressByTypeRef.current[type] = currentValue;
      const achievement = achievementsObjRef.current?.[type];
      showToast({
        kind: 'achievement',
        mode: 'progress',
        title: achievement?.title || 'Achievement',
        badgeSrc: getAchievementBadge(type),
        currentValue,
        targetValue,
        prevValue: prev,
        delta,
        linkTo: `/achievements/${type}`,
        duration: 5200
      });
    }

    function handleNewPost({ comment, target }: { comment: any; target: any }) {
      if (comment) {
        if (target.commentId || target.replyId) {
          onUploadReply({
            ...target,
            ...comment
          });
        } else {
          onUploadComment({
            contentId: target.contentId,
            contentType: target.contentType,
            ...comment
          });
        }
      }
      onIncreaseNumNewPosts();
    }

    function handleNewRecommendation({
      uploaderId,
      recommendations,
      recommenderId,
      target
    }: {
      uploaderId: number;
      recommendations: any[];
      recommenderId: number;
      target: { contentId: number; contentType: string };
    }) {
      if (
        recommenderId === userId ||
        uploaderId === userId ||
        shouldUpdateRecommendations(target)
      ) {
        onRecommendContent({
          recommendations,
          contentId: target.contentId,
          contentType: target.contentType
        });
      }
    }

    function shouldUpdateRecommendations(target: {
      contentId: number;
      contentType: string;
    }) {
      const { contentId, contentType } = target;
      const targetKey = `${contentType}${contentId}`;
      const currentContentState = getContentStateSnapshot();
      if (currentContentState[targetKey]) {
        return true;
      }
      if (contentType === 'comment') {
        return commentExistsInState(Number(contentId));
      }
      return Object.values(currentContentState).some((value: any) => {
        if (!value || typeof value !== 'object') return false;
        if (
          value.contentType === contentType &&
          Number(value.contentId) === Number(contentId)
        ) {
          return true;
        }
        if (
          contentType === 'subject' &&
          Array.isArray(value.subjects) &&
          value.subjects.some(
            (subject: any) => Number(subject?.id) === Number(contentId)
          )
        ) {
          return true;
        }
        return false;
      });
    }

    function commentExistsInState(commentId: number) {
      const id = Number(commentId);
      const currentContentState = getContentStateSnapshot();
      return Object.values(currentContentState).some((value: any) =>
        commentIsNested(value, id, new Set<any>())
      );
    }

    function commentIsNested(
      container: any,
      commentId: number,
      visited: Set<any>
    ): boolean {
      if (!container || typeof container !== 'object' || visited.has(container))
        return false;
      visited.add(container);

      if (
        (!container.contentType || container.contentType === 'comment') &&
        Number(container.id) === commentId
      ) {
        return true;
      }

      if (
        Array.isArray(container.comments) &&
        container.comments.some((comment: any) =>
          commentIsNested(comment, commentId, visited)
        )
      ) {
        return true;
      }

      if (
        Array.isArray(container.replies) &&
        container.replies.some((reply: any) =>
          commentIsNested(reply, commentId, visited)
        )
      ) {
        return true;
      }

      if (
        Array.isArray(container.subjects) &&
        container.subjects.some((subject: any) =>
          commentIsNested(subject, commentId, visited)
        )
      ) {
        return true;
      }

      if (container.targetObj && typeof container.targetObj === 'object') {
        if (
          Object.values(container.targetObj).some((value: any) =>
            commentIsNested(value, commentId, visited)
          )
        ) {
          return true;
        }
      }

      if (container.rootObj && typeof container.rootObj === 'object') {
        if (commentIsNested(container.rootObj, commentId, visited)) {
          return true;
        }
      }

      return false;
    }

    async function handleNewReward({
      target,
      reward,
      receiverId
    }: {
      target: any;
      reward: any;
      receiverId: number;
    }) {
      if (reward.rewarderId !== userId) {
        onAttachReward({
          reward,
          contentId: target.contentId,
          contentType: target.contentType
        });
      }
      if (receiverId === userId) {
        onSetRewardsTimeoutExecuted(false);

        const { currentChatSubject, loadMoreNotifications, notifications } =
          await fetchNotifications();

        const {
          rewards,
          loadMoreRewards,
          totalRewardedTwinkles,
          totalRewardedTwinkleCoins
        } = await loadRewards();

        onLoadRewards({
          rewards,
          loadMoreRewards,
          totalRewardedTwinkles,
          totalRewardedTwinkleCoins,
          userId
        });
        onLoadNotifications({
          currentChatSubject,
          loadMoreNotifications,
          notifications,
          userId
        });
      }
    }

    function handleUpdateMyCoins() {
      void applyCanonicalCoinsAndReconcile({
        loadCoins,
        onSetUserState,
        userId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
}
